import * as THREE from 'three';

/**
 * Preetham analytic sky — GLSL *and* a matching CPU evaluator.
 *
 * The two implementations below are deliberately line-for-line equivalent. That
 * is the whole point of this file: `Lighting.js` derives the sun colour, the
 * ambient fill and the fog colour from the *same* radiance function the sky
 * shader uses, so the sun on screen and the sun lighting the world can never
 * disagree. Mismatched sky/sun colour is the single most common tell of an
 * amateur real-time scene.
 *
 * All the scattering coefficients (betaR, betaM, sunE) are computed once on the
 * CPU and pushed to the shader as uniforms, so there is exactly one source of
 * truth for them.
 *
 * Output is **linear HDR radiance**. Tone mapping happens later (OutputPass, or
 * the renderer when drawing straight to screen — the shader includes the
 * standard chunks so both paths are correct).
 */

/* ------------------------------- constants -------------------------------- */

// Rayleigh scattering at sea level for 680/550/450 nm.
const TOTAL_RAYLEIGH = [5.804542996261093e-6, 1.3562911419845635e-5, 3.0265902468824876e-5];
// Mie scattering constants for the same wavelengths.
const MIE_CONST = [1.8399918514433978e14, 2.7798023919660528e14, 4.0790479543861094e14];

const RAYLEIGH_ZENITH_LENGTH = 8.4e3;
const MIE_ZENITH_LENGTH = 1.25e3;
const SUN_CUTOFF_ANGLE = 1.6110731556870734;
const SUN_STEEPNESS = 1.5;
const SUN_E = 1000.0;
const THREE_OVER_SIXTEEN_PI = 0.05968310365946075;
const ONE_OVER_FOUR_PI = 0.07957747154594767;
// Brings Preetham's absurd units into a range ACES tone mapping likes.
const RADIANCE_SCALE = 0.04;

const clamp = (x, a, b) => (x < a ? a : x > b ? b : x);
const mix = (a, b, t) => a + (b - a) * t;

/* --------------------------------- state ---------------------------------- */

/**
 * Turns art-directable parameters into the physical coefficients both the
 * shader and the CPU evaluator run on.
 *
 * @param {object} p
 * @param {number} p.sunElevation  degrees above the horizon
 * @param {number} p.sunAzimuth    degrees, 0 = -Z, increasing clockwise
 * @param {number} p.turbidity     haze / dust load. 2 = alpine, 10 = smoggy city
 * @param {number} p.rayleigh      blue-scattering multiplier
 * @param {number} p.mieCoefficient aerosol density
 * @param {number} p.mieDirectionalG forward-scattering anisotropy (0..1)
 */
export function computeSkyState(p) {
  const phi = THREE.MathUtils.degToRad(90 - p.sunElevation); // polar from +Y
  const theta = THREE.MathUtils.degToRad(p.sunAzimuth);
  const sunDirection = new THREE.Vector3().setFromSphericalCoords(1, phi, theta).normalize();

  const sunE = sunIntensity(sunDirection.y);

  const betaR = new THREE.Vector3(
    TOTAL_RAYLEIGH[0] * p.rayleigh,
    TOTAL_RAYLEIGH[1] * p.rayleigh,
    TOTAL_RAYLEIGH[2] * p.rayleigh,
  );

  // totalMie(): 0.434 * c * MieConst, c = (0.2 * T) * 10E-18
  const c = 0.2 * p.turbidity * 1e-17;
  const betaM = new THREE.Vector3(
    0.434 * c * MIE_CONST[0] * p.mieCoefficient,
    0.434 * c * MIE_CONST[1] * p.mieCoefficient,
    0.434 * c * MIE_CONST[2] * p.mieCoefficient,
  );

  return { sunDirection, sunE, betaR, betaM, mieG: p.mieDirectionalG, params: p };
}

function sunIntensity(zenithAngleCos) {
  const c = clamp(zenithAngleCos, -1, 1);
  return SUN_E * Math.max(0, 1 - Math.exp(-((SUN_CUTOFF_ANGLE - Math.acos(c)) / SUN_STEEPNESS)));
}

/** Relative optical mass along a view ray, from its cosine to zenith. */
function opticalInverse(cosZenith) {
  const zenithAngle = Math.acos(Math.max(0, Math.min(1, cosZenith)));
  const deg = (zenithAngle * 180) / Math.PI;
  return 1 / (Math.cos(zenithAngle) + 0.15 * Math.pow(93.885 - deg, -1.253));
}

/**
 * In-scattered radiance for a view direction — the CPU twin of `skyRadiance()`
 * in the fragment shader below. Excludes the sun disc.
 *
 * @returns {THREE.Vector3} linear HDR radiance
 */
export function skyRadiance(dir, s, out = new THREE.Vector3()) {
  const inv = opticalInverse(dir.y);
  const sR = RAYLEIGH_ZENITH_LENGTH * inv;
  const sM = MIE_ZENITH_LENGTH * inv;

  const cosTheta = dir.dot(s.sunDirection);
  const rPhase = rayleighPhase(cosTheta * 0.5 + 0.5);
  const mPhase = hgPhase(cosTheta, s.mieG);

  // Horizon "sunset gradient" term — strongest when the sun itself is low.
  const sunLow = clamp(Math.pow(1 - s.sunDirection.y, 5), 0, 1);

  const br = [s.betaR.x, s.betaR.y, s.betaR.z];
  const bm = [s.betaM.x, s.betaM.y, s.betaM.z];
  const rgb = [0, 0, 0];

  for (let i = 0; i < 3; i++) {
    const fex = Math.exp(-(br[i] * sR + bm[i] * sM));
    const ratio = (br[i] * rPhase + bm[i] * mPhase) / (br[i] + bm[i]);
    let lin = Math.pow(s.sunE * ratio * (1 - fex), 1.5);
    lin *= mix(1, Math.sqrt(Math.max(0, s.sunE * ratio * fex)), sunLow);
    rgb[i] = lin * RADIANCE_SCALE;
  }
  return out.set(rgb[0], rgb[1], rgb[2]);
}

/**
 * Direct (transmitted) sunlight reaching the ground — the colour a directional
 * light standing in for the sun must have. Low sun => long path => deep amber.
 *
 * @returns {THREE.Vector3} linear HDR radiance
 */
export function sunTransmittance(s, out = new THREE.Vector3()) {
  const inv = opticalInverse(s.sunDirection.y);
  const sR = RAYLEIGH_ZENITH_LENGTH * inv;
  const sM = MIE_ZENITH_LENGTH * inv;
  const br = [s.betaR.x, s.betaR.y, s.betaR.z];
  const bm = [s.betaM.x, s.betaM.y, s.betaM.z];
  return out.set(
    Math.exp(-(br[0] * sR + bm[0] * sM)),
    Math.exp(-(br[1] * sR + bm[1] * sM)),
    Math.exp(-(br[2] * sR + bm[2] * sM)),
  );
}

/**
 * Suspended-dust layer, applied on top of Preetham.
 *
 * Additive haze alone only ever makes the horizon *whiter* — and ACES
 * desaturates bright values, so a purely additive model can never produce an
 * amber horizon, only a grey one. Real aerosol absorbs blue far more than red,
 * so this applies a wavelength-dependent extinction first and adds a much
 * smaller warm in-scatter second. That is what turns the horizon gold.
 *
 * ## Why the dust is direction-dependent
 *
 * Applying the amber extinction uniformly around the compass is wrong, and it
 * was wrong in a specific, visible way: it dragged the *anti-solar* horizon
 * through the crossover between "blue sky" and "amber dust", and the colour
 * halfway between blue and amber is **green**. Measured at 13 degrees
 * elevation, the old model put a [137,155,126] olive band right across the sky
 * behind the player. There is no dusk on Earth that does that.
 *
 * The physics: reddening comes from looking *through* a dust column at the
 * transmitted solar beam, so it belongs on the sun side. Looking away from the
 * sun you see dust lit by multiply-scattered light, which is neutral-to-lilac —
 * the Belt of Venus. So both the extinction spectrum and the in-scatter tint
 * lerp between a warm sun-side set and a cool anti-solar set by `cosSun`.
 *
 * Measured anti-solar horizon after this change: [127,117,141] — lilac grey,
 * with no direction anywhere on the dome where green is the dominant channel.
 *
 * Must stay identical to `applyDust()` in the fragment shader.
 *
 * @param {THREE.Vector3} color  linear radiance, modified in place
 * @param {number} dirY          view direction Y
 * @param {number} cosSun        dot(viewDir, sunDir)
 */
export function applyDust(color, dirY, cosSun, t) {
  const band = Math.pow(Math.max(0, 1 - Math.abs(dirY)), t.dustFalloff);
  const warm = Math.pow(Math.max(0, cosSun), t.dustWarmFocus);
  const e = t.dustExtinction * band;

  const c = [color.x, color.y, color.z];
  for (let i = 0; i < 3; i++) {
    const absorb = mix(t.dustAbsorbAnti[i], DUST_ABSORB[i], warm);
    c[i] *= Math.exp(-e * absorb);
  }

  // In-scatter: mostly forward (the aureole's ground-level cousin), with an
  // isotropic floor so the anti-solar dust is lit at all.
  const k =
    t.dustAmount * band * (t.dustAmbient + (1 - t.dustAmbient) * Math.pow(Math.max(0, cosSun), 1.6));
  for (let i = 0; i < 3; i++) {
    c[i] += mix(t.dustAntiTint[i], t.dustTint[i], warm) * k;
  }

  return color.set(c[0], c[1], c[2]);
}

/** Relative absorption per channel on the **sun side**. Blue is scrubbed
 *  hardest — that is what makes a dusty horizon gold rather than merely pale. */
const DUST_ABSORB = [0.22, 0.66, 1.32];

function rayleighPhase(cosTheta) {
  return THREE_OVER_SIXTEEN_PI * (1 + cosTheta * cosTheta);
}

function hgPhase(cosTheta, g) {
  const g2 = g * g;
  const denom = Math.pow(Math.max(1e-4, 1 - 2 * g * cosTheta + g2), 1.5);
  return (ONE_OVER_FOUR_PI * (1 - g2)) / denom;
}

/* ------------------------------ directional fog ---------------------------- */

/**
 * Makes `scene.fog` a function of view direction instead of one flat colour.
 *
 * ## Why this is a ShaderChunk patch and not `scene.fog.color` per frame
 *
 * `FogExp2` carries a single colour, so the cheap version of this fix is to
 * drive that colour from the camera's forward vector each frame. That fixes
 * nothing at the FOV this game runs: with a ~75 degree horizontal frustum, one
 * frame routinely contains geometry 40 degrees toward the sun and 40 degrees
 * away from it, and a per-frame colour gives both of them the same haze. The
 * defect being fixed here is precisely that the haze disagreed with the sky
 * above it *within a single image*, so the term has to be per-pixel.
 *
 * ## How the direction reaches the fragment shader
 *
 * Adding a uniform to every lit material in the game is not possible from one
 * module — `UniformsUtils.merge` clones, so each material would need its own
 * per-frame write. Instead the sun direction and both haze colours are compiled
 * in as GLSL literals, exactly as `Lighting.js` compiles its cascade splits in,
 * and the only new plumbing is one varying carrying view-space position.
 * `fogColor` stays live and meaningful: it is the mid haze colour, and the two
 * baked tints are ratios around it, so `scene.fog.color` still works as the
 * overall haze brightness/hue knob at runtime.
 *
 * `mvPosition` is guaranteed to exist wherever `<fog_vertex>` is included —
 * three's own vertex shaders all place it after `<project_vertex>` — and using
 * view-space *position* rather than a direction means it interpolates correctly
 * across the large, few-vertex triangles that ground planes are made of.
 *
 * @param {object} p
 * @param {{x:number,y:number,z:number}} p.sunDirection world-space, toward the sun
 * @param {number[]} p.warm  RGB multiplier on `fogColor` looking at the sun
 * @param {number[]} p.cool  RGB multiplier on `fogColor` looking away from it
 * @param {number} p.focus   shaping exponent on the warm/cool blend
 * @returns {boolean} whether the patch is installed
 */
export function installDirectionalFog(p) {
  const store = THREE.ShaderChunk;
  const f = (n) => n.toFixed(5);
  const v3 = (a) => `vec3( ${f(a[0])}, ${f(a[1])}, ${f(a[2])} )`;
  const signature = `${v3([p.sunDirection.x, p.sunDirection.y, p.sunDirection.z])}|${v3(p.warm)}|${v3(
    p.cool,
  )}|${f(p.focus)}`;
  if (store.__obFogSignature === signature) return store.__obFogInstalled === true;

  if (!store.__obPristineFog) {
    store.__obPristineFog = {
      parsVertex: store.fog_pars_vertex,
      vertex: store.fog_vertex,
      parsFragment: store.fog_pars_fragment,
      fragment: store.fog_fragment,
    };
  }
  const pristine = store.__obPristineFog;

  const VERT_ANCHOR = 'vFogDepth = - mvPosition.z;';
  const FRAG_ANCHOR = 'gl_FragColor.rgb = mix( gl_FragColor.rgb, fogColor, fogFactor );';
  if (!pristine.vertex.includes(VERT_ANCHOR) || !pristine.fragment.includes(FRAG_ANCHOR)) {
    console.warn('[sky] three.js fog chunks changed shape — directional fog disabled');
    store.fog_pars_vertex = pristine.parsVertex;
    store.fog_vertex = pristine.vertex;
    store.fog_pars_fragment = pristine.parsFragment;
    store.fog_fragment = pristine.fragment;
    store.__obFogSignature = signature;
    store.__obFogInstalled = false;
    return false;
  }

  store.fog_pars_vertex = pristine.parsVertex.replace(
    'varying float vFogDepth;',
    'varying float vFogDepth;\n\tvarying vec3 vFogViewPos;',
  );
  store.fog_vertex = pristine.vertex.replace(
    VERT_ANCHOR,
    `${VERT_ANCHOR}\n\tvFogViewPos = mvPosition.xyz;`,
  );
  store.fog_pars_fragment = pristine.parsFragment.replace(
    'varying float vFogDepth;',
    'varying float vFogDepth;\n\tvarying vec3 vFogViewPos;',
  );
  store.fog_fragment = pristine.fragment.replace(
    FRAG_ANCHOR,
    `// --- directional atmospheric perspective (see installDirectionalFog) ---
	vec3 obSunView = mat3( viewMatrix ) * ${v3([p.sunDirection.x, p.sunDirection.y, p.sunDirection.z])};
	float obCos = dot( normalize( vFogViewPos ), obSunView );
	float obWarm = pow( clamp( obCos * 0.5 + 0.5, 0.0, 1.0 ), ${f(p.focus)} );
	vec3 obFog = fogColor * mix( ${v3(p.cool)}, ${v3(p.warm)}, obWarm );
	gl_FragColor.rgb = mix( gl_FragColor.rgb, obFog, fogFactor );`,
  );

  store.__obFogSignature = signature;
  store.__obFogInstalled = true;
  return true;
}

/* --------------------------------- shaders -------------------------------- */

export const SKY_VERT = /* glsl */ `
varying vec3 vWorldDir;

void main() {
  vec4 world = modelMatrix * vec4( position, 1.0 );
  vWorldDir = world.xyz - cameraPosition;
  gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
}
`;

export const SKY_FRAG = /* glsl */ `
varying vec3 vWorldDir;

uniform vec3  sunDirection;
uniform float sunE;
uniform vec3  betaR;
uniform vec3  betaM;
uniform float mieG;

uniform float skyExposure;
uniform float sunDiscCos;
uniform float sunDiscIntensity;
uniform float sunGlow;
// Below-horizon fill. See the "below the horizon" block in main() — on the
// visible dome this is a continuation of the horizon radiance, not a
// replacement colour, so there is no third colour to disagree with the sky.
uniform float groundFalloff;   // dir.y depression over which the fill settles
uniform float groundDesat;     // chroma kept at full depression, 0 = neutral grey
uniform float groundGain;      // luminance at full depression
// 1 selects the legacy flat fill below. Only the environment-map copy sets it,
// and deliberately: the whole light rig was solved against the irradiance that
// fill produces. Measured, cosine-weighted, for the copy the IBL is baked from:
//   normal      old b/r   continued b/r   luminance
//   up-facing     1.196       1.196         x1.000
//   sun-facing    0.621       0.740         x1.408
//   anti-solar    0.683       1.192         x1.201
//   down-facing   0.237       0.930         x2.346
// Up-facing is untouched either way (it sees none of the lower hemisphere), so
// the navy-asphalt case is safe — but vertical faces in shade would go from
// b/r 0.68 to 1.19, which is precisely the cool-shade regression the hemisphere
// and env-saturation values were tuned to remove. So the IBL keeps the old fill
// bit for bit, and only the sky you look at changes.
uniform float groundFlat;
uniform vec3  groundColor;     // legacy flat fill (environment copy only)
uniform vec3  dustColor;
uniform vec3  dustAntiColor;
uniform vec3  dustAbsorbAnti;
uniform float dustAmbient;
uniform float dustWarmFocus;
uniform float dustAmount;
uniform float dustExtinction;
uniform float dustFalloff;
// Luminance ceiling. The visible dome runs unclamped so the sun stays hot for
// bloom; the copy the environment map is baked from is clamped hard, because
// the sun's energy is already in the directional light and letting the aureole
// into the IBL floods the whole scene with ambient and kills every shadow.
uniform float radianceClamp;
// Saturation applied to the environment-map copy only, ramped by elevation.
//
// An up-facing surface integrates mostly zenith radiance, and a turbidity-9
// golden-hour zenith is an intensely saturated blue: baked straight into the
// IBL it gives horizontal surfaces a fill of b/r 2.3 and every road in the map
// reads as navy mud, while vertical surfaces (which see the warm horizon) stay
// correct. Desaturating the whole dome fixes the road but flattens the horizon
// band that metals and walls actually reflect — so the ramp keeps the horizon
// near full saturation and pulls only the upper hemisphere toward neutral.
uniform float satHorizon;
uniform float satZenith;
uniform vec2  satRamp; // dir.y where the ramp starts / ends
// Soft shoulder on the in-scattered term, applied before the sun disc is added.
// skyKneeStart is the luminance below which nothing is touched at all — it is
// deliberately above every colour the CPU sampler reads, so the sky the world is
// lit by and the sky you see stay the same numbers.
uniform float skyKneeStart;
uniform float skyKnee;

const float PI = 3.141592653589793;
const float RAYLEIGH_ZENITH_LENGTH = 8.4E3;
const float MIE_ZENITH_LENGTH = 1.25E3;
const float THREE_OVER_SIXTEEN_PI = 0.05968310365946075;
const float ONE_OVER_FOUR_PI = 0.07957747154594767;
const float RADIANCE_SCALE = 0.04;
const vec3  UP = vec3( 0.0, 1.0, 0.0 );

float rayleighPhase( float cosTheta ) {
  return THREE_OVER_SIXTEEN_PI * ( 1.0 + cosTheta * cosTheta );
}

float hgPhase( float cosTheta, float g ) {
  float g2 = g * g;
  float denom = pow( max( 1e-4, 1.0 - 2.0 * g * cosTheta + g2 ), 1.5 );
  return ONE_OVER_FOUR_PI * ( 1.0 - g2 ) / denom;
}

// Optical mass along the view ray (Preetham's empirical fit).
float opticalInverse( float cosZenith ) {
  float zenithAngle = acos( clamp( cosZenith, 0.0, 1.0 ) );
  return 1.0 / ( cos( zenithAngle ) + 0.15 * pow( 93.885 - zenithAngle * 180.0 / PI, -1.253 ) );
}

vec3 extinction( float inv ) {
  return exp( - ( betaR * RAYLEIGH_ZENITH_LENGTH * inv + betaM * MIE_ZENITH_LENGTH * inv ) );
}

// Suspended dust. Extinction first (blue absorbed hardest on the sun side,
// which is what makes the horizon amber instead of merely brighter), warm
// in-scatter second. Both terms lerp to a near-neutral, faintly lilac
// anti-solar set: reddening is a property of looking through dust *at the sun*,
// and applying it all the way round the compass put a green band across the
// anti-solar sky (blue sky -> amber dust crosses through green).
// Must stay identical to applyDust() in sky.glsl.js.
const vec3 DUST_ABSORB = vec3( 0.22, 0.66, 1.32 );

vec3 applyDust( vec3 color, float dirY, float cosSun ) {
  float band = pow( clamp( 1.0 - abs( dirY ), 0.0, 1.0 ), dustFalloff );
  float warm = pow( max( cosSun, 0.0 ), dustWarmFocus );
  color *= exp( - dustExtinction * band * mix( dustAbsorbAnti, DUST_ABSORB, warm ) );
  float scatter = dustAmount * band
    * ( dustAmbient + ( 1.0 - dustAmbient ) * pow( max( cosSun, 0.0 ), 1.6 ) );
  return color + mix( dustAntiColor, dustColor, warm ) * scatter;
}

// In-scattered sky radiance, sun disc excluded. Mirrors skyRadiance() in JS.
vec3 skyRadiance( vec3 dir, float cosTheta, vec3 Fex ) {
  float rPhase = rayleighPhase( cosTheta * 0.5 + 0.5 );
  float mPhase = hgPhase( cosTheta, mieG );
  vec3 ratio = ( betaR * rPhase + betaM * mPhase ) / ( betaR + betaM );

  vec3 Lin = pow( sunE * ratio * ( 1.0 - Fex ), vec3( 1.5 ) );
  float sunLow = clamp( pow( 1.0 - sunDirection.y, 5.0 ), 0.0, 1.0 );
  Lin *= mix( vec3( 1.0 ), sqrt( max( sunE * ratio * Fex, vec3( 0.0 ) ) ), sunLow );

  return Lin * RADIANCE_SCALE;
}

void main() {
  vec3 dir = normalize( vWorldDir );
  float cosTheta = dot( dir, sunDirection );
  float inv = opticalInverse( dir.y );
  vec3 Fex = extinction( inv );

  vec3 color = skyRadiance( dir, cosTheta, Fex );

  color = applyDust( color, dir.y, cosTheta );

  // --- aureole shoulder ----------------------------------------------------
  // The Mie aureole is the reason the sun used to be a formless smear, and the
  // halo term below was never the main offender: measured at 13 degrees
  // elevation the *scattering* term alone was above 4.0 linear — pure white
  // through ACES — out to 15 degrees from the sun, which at a 25 degree FOV is
  // a 645 px radius of flat white that swallowed every midground silhouette on
  // the sun side.
  //
  // A hard clamp would only trade the smear for a flat disc of clipped colour,
  // so this is a soft shoulder: everything below skyKneeStart is untouched
  // (which is the entire horizon band, the fill and the fog, so none of the
  // colours other systems sample move at all), and above it the *luminance* is
  // compressed hue-preservingly toward skyKnee. The gradient survives, the
  // blown-out core shrinks to ~6 degrees, and the disc — added afterwards, so
  // never compressed — finally sits above its own surroundings.
  {
    float lum = dot( color, vec3( 0.2126, 0.7152, 0.0722 ) );
    float over = max( lum - skyKneeStart, 0.0 );
    // The base has to be min(lum, skyKneeStart), not skyKneeStart on its own:
    // with a bare constant every sky dimmer than the knee was scaled *up* to
    // it, washing the whole dome pale — a flat grey sky away from the sun.
    float rolled = min( lum, skyKneeStart )
      + over / ( 1.0 + over / max( skyKnee - skyKneeStart, 1e-3 ) );
    color *= rolled / max( lum, 1e-5 );
  }

  // --- sun disc + glow -----------------------------------------------------
  // The disc is left deliberately hot so the bloom pass has something real to
  // catch. The glow lobes are now a thin limb bleed only: with the shoulder
  // above doing the heavy lifting, a fat inner lobe would just rebuild the
  // smear it was added to remove.
  float aboveHorizon = smoothstep( -0.02, 0.03, sunDirection.y );
  float disc = smoothstep( sunDiscCos, mix( sunDiscCos, 1.0, 0.06 ), cosTheta );
  float halo = pow( max( cosTheta, 0.0 ), 3200.0 ) * 0.10
             + pow( max( cosTheta, 0.0 ), 160.0 ) * 0.035
             + pow( max( cosTheta, 0.0 ), 14.0 ) * 0.010;
  vec3 sunTint = normalize( max( Fex, vec3( 1e-4 ) ) ) * 1.732;
  color += sunTint * ( disc * sunDiscIntensity + halo * sunGlow * sunE * RADIANCE_SCALE * 2.0 ) * aboveHorizon;

  // --- below the horizon ---------------------------------------------------
  // Preetham is undefined down here, but it does not need a *replacement* — it
  // needs continuing. opticalInverse() already clamps its cosine to zero, so
  // for every dir.y < 0 the in-scatter term is frozen at exactly the horizon
  // value for that azimuth. That is the right starting point: the fill below
  // the skyline is the same air, seen through a longer path.
  //
  // What used to happen instead was mix( groundColor, color, smoothstep(
  // -0.055, 0.035, dir.y ) ) — a hard 5-degree crossfade into one flat,
  // azimuth-independent colour sampled from the whole horizon ring and then
  // pushed further orange by groundTint. Two things made that read as a
  // painted band rather than as distance: it was *flat* (the sky above it swings
  // from amber on the sun side to lilac behind you, and the fill under it did
  // not), and it was *saturated* (haze loses chroma with depth, it does not gain
  // it). Both are worse now that fogDensity dropped 0.0088 -> 0.004, because a
  // rooftop view no longer buries the band in fog.
  //
  // So: keep the horizon's own colour — including its azimuth swing — and take
  // it down the way real terrain-under-haze goes, by losing chroma and a little
  // luminance with depression angle. No edge anywhere to see.
  {
    float depression = clamp( - dir.y, 0.0, 1.0 );
    float t = smoothstep( 0.0, groundFalloff, depression );
    float lum = dot( color, vec3( 0.2126, 0.7152, 0.0722 ) );
    vec3 haze = mix( color, vec3( lum ), groundDesat ) * groundGain;
    vec3 continued = mix( color, haze, t );
    vec3 legacy = mix( groundColor, color, smoothstep( -0.055, 0.035, dir.y ) );
    color = mix( continued, legacy, groundFlat );
  }

  color *= skyExposure;

  float grey = dot( color, vec3( 0.2126, 0.7152, 0.0722 ) );
  float sat = mix( satHorizon, satZenith, smoothstep( satRamp.x, satRamp.y, dir.y ) );
  color = mix( vec3( grey ), color, sat );

  // Hue-preserving luminance clamp (see radianceClamp above).
  float lum = dot( color, vec3( 0.2126, 0.7152, 0.0722 ) );
  if ( lum > radianceClamp ) color *= radianceClamp / lum;

  gl_FragColor = vec4( color, 1.0 );

  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;
